'use client';

import * as React from 'react';
import {useInView} from 'react-intersection-observer';
import {ImageWithFallback} from '@purpose/ui/components/ImageWithFallback';
import {cn} from '@purpose/ui/lib/utils';

interface ContentCardProps {
  width?: number | string;
  image: string | null;
  title?: string;
  description?: string;
  subtitle?: string;
  onClick?: () => void;
  priority?: boolean;
  overtitle?: React.JSX.Element;
}

const ContentCard = React.forwardRef<HTMLDivElement, ContentCardProps>(
  (
    {
      width,
      image,
      title,
      description,
      subtitle,
      onClick,
      priority = false,
      overtitle = <></>
    },
    ref
  ) => {
    // Use intersection observer to determine if card is in view
    const {ref: inViewRef, inView} = useInView({
      threshold: 0.1,
      triggerOnce: true
    });

    // Combine refs
    const combinedRef = React.useMemo(() => {
      return (node: HTMLDivElement | null) => {
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        inViewRef(node);
      };
    }, [ref, inViewRef]);

    return (
      <div
        ref={combinedRef}
        className={cn(
          'bg-card relative flex w-full flex-col gap-1 overflow-hidden rounded-lg',
          onClick
            ? 'cursor-pointer transition-shadow hover:shadow-md'
            : 'cursor-default'
        )}
        {...(onClick && {onClick})}
      >
        <div className="card-container relative aspect-[4/3] overflow-hidden rounded-lg">
          {image && (
            <ImageWithFallback
              src={image}
              alt={title || 'alt'}
              fill
              className="card-image h-full w-full object-cover"
              priority={priority || inView}
              loading={priority || inView ? 'eager' : 'lazy'}
              placeholder="empty"
              quality={80}
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw"
              iconStyles={{
                width: 64,
                height: 64
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-1 p-2">
          {overtitle}
          {title && (
            <h3 className="text-card-foreground line-clamp-2 text-sm font-semibold tracking-tight sm:text-base lg:text-lg">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-muted-foreground line-clamp-1 text-xs font-medium sm:text-sm">
              {subtitle}
            </p>
          )}
          {description && (
            <p className="text-muted-foreground line-clamp-2 text-xs sm:text-sm">
              {description}
            </p>
          )}
        </div>
      </div>
    );
  }
);

ContentCard.displayName = 'ContentCard';

export {ContentCard};
export type {ContentCardProps};
